import { DsAlert, DsBadge, DsButton, DsDialog, EMPTY_STATE_CLASSES } from '@frontends/ds';
import { DsIcon } from '@frontends/ds-icons';
import { cn } from '@frontends/util-cn';
import { useLocale } from '@frontends/util-i18n';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { showToast } from '@/components/Toast.tsx';
import useAxiosInterceptors from '@/hooks/useAxiosInterceptors.tsx';
import { useEntityConfig } from '@/hooks/useEntityConfig';
import { replaceLanguageInUrl } from '@/lib/utils';
import { type ClientSession } from '@/types/Auth.ts';
import { type IGenericError } from '@/types/Error.ts';
import { SessionDetailsDialog } from './Modules/SessionDetailsDialog';
import { SessionItem } from './Modules/SessionItem';
import { SessionSummary } from './Modules/SessionSummary';

const SESSIONS_QUERY_KEY = ['sessions-data'];

const CARD_CLASSES = 'overflow-hidden rounded-2xl border border-borderSurface bg-backgroundRaised';
const LIST_CLASSES = 'divide-y divide-borderPrimary';
const SECTION_TITLE_CLASSES = 'text-base font-medium text-textSecondary';

type Confirmation = { kind: 'session'; session: ClientSession } | { kind: 'others' };

const getLastUsedTime = (session: ClientSession) => Date.parse(session.last_used) || 0;

function SessionsSkeleton() {
  return (
    <ul aria-hidden className={cn(CARD_CLASSES, LIST_CLASSES)}>
      {[0, 1, 2].map((index) => (
        <li key={index} className="flex items-center gap-3 px-4 py-3">
          <span className="size-10 shrink-0 rounded-md bg-backgroundHover motion-safe:animate-pulse" />
          <span className="flex flex-1 flex-col gap-2">
            <span className="h-3 w-2/5 rounded-sm bg-backgroundHover motion-safe:animate-pulse" />
            <span className="h-3 w-3/5 rounded-sm bg-backgroundHover motion-safe:animate-pulse" />
          </span>
        </li>
      ))}
    </ul>
  );
}

const Sessions = memo(function Sessions() {
  const { t } = useTranslation();
  const axiosPrivate = useAxiosInterceptors();
  const queryClient = useQueryClient();
  const config = useEntityConfig();
  const localeContext = useLocale();

  const [detailsSession, setDetailsSession] = useState<ClientSession | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [confirmation, setConfirmation] = useState<Confirmation | null>(null);
  const [isConfirmationOpen, setIsConfirmationOpen] = useState(false);
  const otherSessionsTitleRef = useRef<HTMLHeadingElement>(null);
  const shouldFocusOtherSessionsRef = useRef(false);

  const sessionsQuery = useQuery<ClientSession[], IGenericError>({
    queryKey: SESSIONS_QUERY_KEY,
    queryFn: async () => {
      const response = await axiosPrivate.get<ClientSession[] | null>('/sessions');

      return response.data ?? [];
    },
  });

  const getErrorDescription = (error: IGenericError) => {
    const translationKey = error.context?.translationKey;

    return translationKey ? t(translationKey, { defaultValue: error.detail }) : error.detail;
  };

  const refetchSessions = () => queryClient.invalidateQueries({ queryKey: SESSIONS_QUERY_KEY });

  const keepSessions = (predicate: (session: ClientSession) => boolean) =>
    queryClient.setQueryData<ClientSession[]>(SESSIONS_QUERY_KEY, (sessions) => sessions?.filter(predicate));

  const endSession = useMutation<unknown, IGenericError, ClientSession>({
    mutationFn: (session) => axiosPrivate.delete(`/sessions/${session.session_id}`),
    onSuccess: (_, endedSession) => {
      keepSessions((session) => session.session_id !== endedSession.session_id);
      shouldFocusOtherSessionsRef.current = true;
      showToast({ type: 'success', title: t('sessions_session_ended'), description: '' });
    },
    onError: (error) => {
      showToast({
        type: 'error',
        title: t('sessions_end_session_failed'),
        description: getErrorDescription(error),
      });
    },
    onSettled: () => {
      setIsConfirmationOpen(false);
      refetchSessions();
    },
  });

  const endOtherSessions = useMutation<unknown, IGenericError>({
    mutationFn: () => axiosPrivate.delete('/sessions'),
    onSuccess: () => {
      keepSessions((session) => Boolean(session.metadata?.is_current));
      shouldFocusOtherSessionsRef.current = true;
      showToast({ type: 'success', title: t('sessions_other_sessions_ended'), description: '' });
    },
    onError: (error) => {
      showToast({
        type: 'error',
        title: t('sessions_end_other_sessions_failed'),
        description: getErrorDescription(error),
      });
    },
    onSettled: () => {
      setIsConfirmationOpen(false);
      refetchSessions();
    },
  });

  useEffect(() => {
    if (isConfirmationOpen || !shouldFocusOtherSessionsRef.current) return;

    shouldFocusOtherSessionsRef.current = false;
    otherSessionsTitleRef.current?.focus();
  }, [isConfirmationOpen]);

  const isEnding = endSession.isPending || endOtherSessions.isPending;
  const isListLoading =
    sessionsQuery.isPending || (sessionsQuery.isError && sessionsQuery.isFetching && !sessionsQuery.data);
  const isListError = sessionsQuery.isError && !sessionsQuery.isFetching;

  const { currentSession, otherSessions } = useMemo(() => {
    const sessions = sessionsQuery.data ?? [];

    return {
      currentSession: sessions.find((session) => session.metadata?.is_current),
      otherSessions: sessions
        .filter((session) => !session.metadata?.is_current)
        .sort((a, b) => getLastUsedTime(b) - getLastUsedTime(a)),
    };
  }, [sessionsQuery.data]);

  const listedDetailsSession = sessionsQuery.data?.find((session) => session.session_id === detailsSession?.session_id);

  const openDetails = (session: ClientSession) => {
    setDetailsSession(session);
    setIsDetailsOpen(true);
  };

  const askConfirmation = (nextConfirmation: Confirmation) => {
    setIsDetailsOpen(false);
    setConfirmation(nextConfirmation);
    setIsConfirmationOpen(true);
  };

  const confirmEnding = () => {
    if (confirmation?.kind === 'session') {
      endSession.mutate(confirmation.session);
    } else {
      endOtherSessions.mutate();
    }
  };

  const openSecuritySettings = () => {
    window.open(replaceLanguageInUrl(config.VITE_SECURITY_URL, localeContext), '_blank', 'noopener,noreferrer');
  };

  const isSessionConfirmation = confirmation?.kind === 'session';

  return (
    <div className="flex max-w-2xl flex-col gap-6">
      <p className="text-base text-textSecondary">{t('sessions_description')}</p>

      <p role="status" className="sr-only">
        {isListLoading ? t('sessions_loading') : ''}
      </p>

      {isListError && (
        <DsAlert
          variant="danger"
          title={t('sessions_load_failed')}
          description={getErrorDescription(sessionsQuery.error)}
          actionLabel={t('retry')}
          onAction={() => sessionsQuery.refetch()}
        />
      )}

      {isListLoading && <SessionsSkeleton />}

      {currentSession && (
        <section className="flex flex-col gap-2">
          <h2 className={SECTION_TITLE_CLASSES}>{t('sessions_your_current_session')}</h2>
          <div className={CARD_CLASSES}>
            <ul>
              <SessionItem session={currentSession} isCurrent onOpenDetails={() => openDetails(currentSession)} />
            </ul>

            {otherSessions.length > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 border-t border-borderPrimary px-4 py-3">
                <DsButton
                  variant="destructive"
                  size="sm"
                  iconStart="log-out"
                  onPress={() => askConfirmation({ kind: 'others' })}
                >
                  {t('sessions_end_other_sessions')}
                </DsButton>
                <span className="text-sm text-textSecondary">{t('sessions_this_device_stays')}</span>
              </div>
            )}
          </div>
        </section>
      )}

      {sessionsQuery.isSuccess && (
        <section className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <h2 ref={otherSessionsTitleRef} tabIndex={-1} className={SECTION_TITLE_CLASSES}>
              {t('sessions_other_sessions')}
            </h2>
            {otherSessions.length > 0 && <DsBadge size="sm">{otherSessions.length}</DsBadge>}
          </div>

          {otherSessions.length > 0 ? (
            <ul className={cn(CARD_CLASSES, LIST_CLASSES)}>
              {otherSessions.map((session) => (
                <SessionItem
                  key={session.session_id}
                  session={session}
                  onOpenDetails={() => openDetails(session)}
                  onEnd={() => askConfirmation({ kind: 'session', session })}
                />
              ))}
            </ul>
          ) : (
            <div className={cn(CARD_CLASSES, EMPTY_STATE_CLASSES, 'flex-col gap-1 py-8')}>
              <DsIcon name="shield-check" size="lg" className="mb-1 text-textSuccessPrimary" />
              <p className="font-medium text-textPrimary">{t('sessions_no_other_sessions')}</p>
              <p>{t('sessions_no_other_sessions_desc')}</p>
            </div>
          )}
        </section>
      )}

      {sessionsQuery.isSuccess && (
        <DsAlert
          role="note"
          variant="neutral"
          icon="shield-check"
          title={t('sessions_security_title')}
          description={t('sessions_security_desc')}
          actionLabel={t('sessions_open_security')}
          onAction={openSecuritySettings}
        />
      )}

      <SessionDetailsDialog
        session={listedDetailsSession ?? detailsSession}
        isOpen={isDetailsOpen && Boolean(listedDetailsSession)}
        onOpenChange={setIsDetailsOpen}
        onEnd={(session) => askConfirmation({ kind: 'session', session })}
      />

      <DsDialog
        isOpen={isConfirmationOpen}
        onOpenChange={(isOpen) => {
          if (!isEnding) setIsConfirmationOpen(isOpen);
        }}
        role="alertdialog"
        size="sm"
        title={isSessionConfirmation ? t('sessions_end_session_title') : t('sessions_end_other_sessions_title')}
        primaryAction={{
          label: isSessionConfirmation ? t('sessions_end_session') : t('sessions_end_other_sessions'),
          variant: 'destructive',
          isPending: isEnding,
          onPress: confirmEnding,
        }}
        secondaryAction={{ label: t('cancel'), isDisabled: isEnding, onPress: () => setIsConfirmationOpen(false) }}
      >
        {confirmation?.kind === 'session' ? (
          <>
            <SessionSummary session={confirmation.session} />
            <p>{t('sessions_end_session_desc')}</p>
          </>
        ) : (
          <p>{t('sessions_end_other_sessions_desc')}</p>
        )}
      </DsDialog>
    </div>
  );
});

export default Sessions;
